/**
 * Excel's AutoFilter, for the tables in this application.
 *
 * The people using this system live in Excel. They expect a column heading to
 * be a control: click it to sort, open it to tick off the values you want to
 * see, and be told how many rows are hidden. Anything less and the first thing
 * they do with a screen is export it and open it in Excel — which is exactly
 * the habit MASTER_PROMPT section 7 says to absorb rather than fight.
 *
 * Shape of the thing:
 *
 *   const sheet = useSheet(rows, COLUMNS);
 *   <table><SheetHead sheet={sheet} /><tbody>{sheet.rows.map(…)}</tbody></table>
 *
 * The body keeps its own rendering. That matters: the stock table marks a
 * negative balance with a chevron, the variance queue tints a row, the loom
 * grid is not a table at all. A component that owned the cells would have to
 * grow an escape hatch for each of those and would still be wrong for the
 * next one. So this owns the ordering, the filtering and the header, and
 * nothing else.
 *
 * Sorting is three-state — ascending, descending, then back to the order the
 * server sent, which is usually meaningful (worst variance first, newest
 * first). Excel has no way back to the original order and people work around
 * it by reloading; that is a wart worth not copying.
 */

import { useCallback, useMemo, useState } from "react";

export type ColumnType = "text" | "number" | "date";

export interface SheetColumn<T> {
  /** Stable key. Used for sort and filter state, and as the export heading. */
  key: string;
  label: string;
  /** Governs comparison and alignment. Defaults to text. */
  type?: ColumnType;
  /**
   * The value to sort, filter and export by — not what is drawn. A column
   * showing "▼ 1,234.500 kg" sorts on the number 1234.5.
   */
  value: (row: T) => string | number | null | undefined;
  /** Overrides `value` for display in the filter list and the export. */
  format?: (row: T) => string;
  /** Right-aligns the header to match a numeric column. */
  numeric?: boolean;
  /** Set false for a column where ticking values off makes no sense. */
  filterable?: boolean;
  sortable?: boolean;
  /** How the column is worked out — an (i) beside the heading. */
  info?: string;
}

export type SortDirection = "asc" | "desc" | null;

export interface SheetState<T> {
  /** Rows after filtering and sorting — what the body should render. */
  rows: T[];
  /** Every row that came in, for the "3 of 47" counter. */
  total: number;
  columns: SheetColumn<T>[];
  sortKey: string | null;
  sortDirection: SortDirection;
  toggleSort: (key: string) => void;
  /** Selected values per column. An absent key means "no filter". */
  filters: Record<string, Set<string>>;
  setFilter: (key: string, values: Set<string> | null) => void;
  clearFilters: () => void;
  activeFilterCount: number;
  /** Distinct values for a column, computed against the *other* filters —
   *  which is what Excel does, and why a filter never empties its own list. */
  optionsFor: (key: string) => string[];
  search: string;
  setSearch: (value: string) => void;
}

const BLANK = "(blank)";

function displayValue<T>(column: SheetColumn<T>, row: T): string {
  if (column.format) return column.format(row);
  const raw = column.value(row);
  if (raw === null || raw === undefined || raw === "") return BLANK;
  return String(raw);
}

function compare<T>(a: T, b: T, column: SheetColumn<T>): number {
  const left = column.value(a);
  const right = column.value(b);

  // Blanks sort last in both directions. A column of numbers with three gaps
  // in it should not open with the three gaps.
  const leftBlank = left === null || left === undefined || left === "";
  const rightBlank = right === null || right === undefined || right === "";
  if (leftBlank && rightBlank) return 0;
  if (leftBlank) return 1;
  if (rightBlank) return -1;

  if (column.type === "number") {
    return Number(left) - Number(right);
  }
  if (column.type === "date") {
    // ISO dates sort correctly as text, which is why the API speaks them.
    return String(left) < String(right) ? -1 : String(left) > String(right) ? 1 : 0;
  }
  // `localeCompare` with `numeric` so "Loom 2" comes before "Loom 10" —
  // plain string order puts 10 first and looks broken to everyone.
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export function useSheet<T>(
  rows: T[],
  columns: SheetColumn<T>[]
): SheetState<T> {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const [search, setSearch] = useState("");

  const toggleSort = useCallback((key: string) => {
    setSortKey((currentKey) => {
      setSortDirection((currentDirection) => {
        if (currentKey !== key) return "asc";
        if (currentDirection === "asc") return "desc";
        return null; // third click: back to the order the server sent
      });
      return key;
    });
  }, []);

  const setFilter = useCallback((key: string, values: Set<string> | null) => {
    setFilters((current) => {
      const next = { ...current };
      if (!values || values.size === 0) delete next[key];
      else next[key] = values;
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setSearch("");
  }, []);

  const byKey = useMemo(() => {
    const map: Record<string, SheetColumn<T>> = {};
    for (const column of columns) map[column.key] = column;
    return map;
  }, [columns]);

  /** Rows passing every filter except the one named — Excel's own rule. */
  const passing = useCallback(
    (except: string | null): T[] => {
      const needle = search.trim().toLowerCase();
      return rows.filter((row) => {
        for (const [key, values] of Object.entries(filters)) {
          if (key === except) continue;
          const column = byKey[key];
          if (!column) continue;
          if (!values.has(displayValue(column, row))) return false;
        }
        if (needle) {
          const hit = columns.some((column) =>
            displayValue(column, row).toLowerCase().includes(needle)
          );
          if (!hit) return false;
        }
        return true;
      });
    },
    [rows, filters, search, byKey, columns]
  );

  const optionsFor = useCallback(
    (key: string): string[] => {
      const column = byKey[key];
      if (!column) return [];
      const seen = new Set<string>();
      for (const row of passing(key)) seen.add(displayValue(column, row));
      const list = [...seen];
      list.sort((a, b) => {
        if (a === BLANK) return 1;
        if (b === BLANK) return -1;
        if (column.type === "number") return Number(a) - Number(b);
        return a.localeCompare(b, undefined, { numeric: true,
                                               sensitivity: "base" });
      });
      return list;
    },
    [byKey, passing]
  );

  const visible = useMemo(() => {
    const filtered = passing(null);
    if (!sortKey || !sortDirection) return filtered;
    const column = byKey[sortKey];
    if (!column) return filtered;
    // A copy: sorting the array in place would mutate what the caller holds
    // and make the third click — back to source order — impossible.
    const sorted = [...filtered];
    sorted.sort((a, b) => compare(a, b, column));
    if (sortDirection === "desc") sorted.reverse();
    return sorted;
  }, [passing, sortKey, sortDirection, byKey]);

  return {
    rows: visible,
    total: rows.length,
    columns,
    sortKey,
    sortDirection,
    toggleSort,
    filters,
    setFilter,
    clearFilters,
    activeFilterCount:
      Object.keys(filters).length + (search.trim() ? 1 : 0),
    optionsFor,
    search,
    setSearch,
  };
}

export { BLANK, displayValue };

/**
 * Everything on screen, as a file Excel opens.
 *
 * CSV rather than a real `.xlsx`: writing the zip container an xlsx needs
 * would mean a new dependency, and MASTER_PROMPT non-negotiable 13 says no new
 * build step without a recorded decision. A UTF-8 CSV with a byte-order mark
 * opens in Excel with the Indian names and the ₹ intact and needs no import
 * wizard, which is the whole of what non-negotiable 11 asks for.
 *
 * What is exported is what is on the screen — filtered and sorted the way the
 * person left it. Exporting the unfiltered set would be a different table from
 * the one they are looking at.
 */
export function exportSheet<T>(
  sheet: SheetState<T>,
  filename: string
): void {
  const escape = (value: string): string =>
    // A leading =, +, - or @ makes Excel treat the cell as a formula. Quoting
    // is not enough; the cell has to stop looking like one.
    /^[=+\-@]/.test(value)
      ? `"'${value.replace(/"/g, '""')}"`
      : `"${value.replace(/"/g, '""')}"`;

  const header = sheet.columns.map((c) => escape(c.label)).join(",");
  const body = sheet.rows.map((row) =>
    sheet.columns
      .map((column) => {
        const raw = column.value(row);
        if (column.type === "number" && raw !== null && raw !== undefined
            && raw !== "") {
          // Unquoted and unformatted, so Excel reads it as a number rather
          // than as text that happens to contain digits and commas.
          return String(raw);
        }
        const shown = displayValue(column, row);
        return escape(shown === BLANK ? "" : shown);
      })
      .join(",")
  );

  const csv = `﻿${[header, ...body].join("\r\n")}\r\n`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  // Firefox ignores a click on an anchor outside the document, and revoking
  // the URL in the same tick can pull the blob out from under the download.
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
